<template>
    <div class="container-fluid">
        <h1>{{$t('controlePageHeader')}}</h1>
        <br />
        <span>{{$t('controlePageText')}}</span>
        <br />
        <br />
        <div class="col-md-10">
            <!-- Parent Card for handling the Verification -->
            <b-card no-body>
                <!-- Parent Card body with buttons -->
                <b-card-body>
                    <!-- Start Verification button -->
                    <base-button :id="`StartCheck`"
                                 :name="`StartCheck`"
                                 class="float-left"
                                 variant="primary"
                                 size="sm"
                                 @click="$_startCheck"
                                 :disabled="startButtonDisabled"
                                 :text="$t('controleStartButtonText')">
                    </base-button>
                    <br />
                    <br />
                    <!-- Child Card for Verification Results  -->
                    <b-card v-show="$_showResults"
                            no-body
                            header-tag="header"
                            footer-tag="footer">
                        <!-- Child Card Header for CSV Download -->
                        <template v-slot:header>
                            <h4>
                                {{$t('controleReport')}}
                            </h4>
                            <div class="float-right">
                                <CsvExport :disabled="!$_errorsFound()"
                                           :dataArray="csvData"
                                           :fileName="'controle_rapport.csv'"
                                           :fieldSeparator="';'"
                                           :title="$t('downloadReport')">
                                </CsvExport>
                            </div>
                        </template>
                        <!-- Child Card Body for Results -->
                        <b-card-body>
                            <span v-show="!$_errorsFound()">
                                {{$t('controleVerificationNoResults')}}
                            </span>
                            <ul class="list-group"
                                v-show="$_errorsFound()">
                                <li class="list-group-item"
                                    v-for="(message, index) in report.messages" v-bind:key="index">
                                    {{message.content}}
                                </li>
                            </ul>
                        </b-card-body>
                    </b-card>
                </b-card-body>
                <!-- Parent Card Footer with status -->
                <template v-slot:footer>
                    <span class="">{{$t('state')}}: {{status.description}}</span>
                </template>
            </b-card>
        </div>
    </div>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import { WEBJOB_STATUS } from '../../constants';
    import CsvExport from '../../components/CsvExport.vue';
    import BaseButton from '../../components/BaseComponents/base-button.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import refcodeMixin from '../../components/Mixins/Refcodes/refcodeMixin.vue';

    export default {
        name: 'Controle',

        mixins: [
            modalMixin,
            refcodeMixin
        ],

        components: {
            CsvExport,
            'base-button': BaseButton
        },

        data() {
            return {
                controleSocket: {},
                // controle process is idle
                controleStateIdle: 0,
                // waiting for the controle process to start
                controleStatePending: 1,
                // controle process is running
                controleStateRunning: 2,

                controleState: this.controleStateIdle,
                organizationId: this.$store.getters.userInfo.organizationId,
                status: {
                    description: '...',
                    code: ''
                },
                stopButtonText: this.$t('controleStopButtonText'),
                report: { messages: [] },
                requestPending: false,
                webJobStatuses: []
            };
        },

        computed: {
            // Download verification results
            csvData() {
                if (this.report.messages) {
                    return this.report.messages.map(message => ({ 'Controle Resultaten': message.content, }));
                }
                return [];
            },

            // Disable start when status is not idle
            startButtonDisabled() {
                return this.controleState !== this.controleStateIdle;
            },

            // Disable stop when status is not
            stopButtonDisabled() {
                return this.controleState !== this.controleStatePending;
            }
        },

        mounted() {
            // Retrieve Language codes
            this.$_initializeWebJobStatuses();
            // Start get messages loop
            this.$_getMessages();
        },

        methods: {

            $_getMessages() {
                const url = `${this.$store.getters.endpoint.replace('https://', 'wss://')}/settings/getwebsocketstatus`;
                this.controleSocket = new WebSocket(url);

                /**
                 * WebSocket on open event handler
                 * @param {object} event object
                 */
                this.controleSocket.onopen = ((event) => {
                    this.$_updateStatus();
                });

                /**
                 * WebSocket on message event handler
                 * @param {object} event object
                 */
                this.controleSocket.onmessage = ((event) => {
                    const previousStatus = this.status;

                    // Read status from websocket/database
                    if (this.webJobStatuses && this.webJobStatuses.length > 0) {
                        this.status = this.getRefCodeFromList(event.data, this.webJobStatuses);
                    }

                    // Verification not in progress
                    if (previousStatus.code !== WEBJOB_STATUS.REQUEST_SENT_PLEASE_WAIT &&
                        (this.status.code === WEBJOB_STATUS.VERIFICATION_FINISHED ||
                            this.status.code === WEBJOB_STATUS.NO_REQUEST_SENT ||
                            this.status.code === WEBJOB_STATUS.VERIFICATION_STOPPED)) {
                        // Stop checking the state of the verification
                        this.controleSocket.close();
                        this.controleState = this.controleStateIdle;
                    } else {
                        // Verification in progress
                        this.controleState = this.controleStateRunning;
                        // Keep updating the state of the verification every 1000 ms
                        setTimeout(() => {
                            this.$_updateStatus();
                        }, 1000);
                    }
                });

                /**
                 * WebSocket on close event handler
                 */
                this.controleSocket.onclose = ((event) => {
                    // Do nothing if process has finished
                    if (this.status.code !== WEBJOB_STATUS.VERIFICATION_FINISHED &&
                        this.status.code !== WEBJOB_STATUS.NO_REQUEST_SENT) {
                        return;
                    }

                    // Get the latest report for this organization
                    this.$_getLatestReport();
                });

                /**
                 * WebSocket on error event handler
                 */
                this.controleSocket.onerror = ((event) => {
                    // Restart the get messages loop
                    this.$_getMessages();
                });
            },

            $_updateStatus() {
                if (this.controleSocket.readyState === this.controleSocket.OPEN) {
                    this.controleSocket.send(this.organizationId);
                }
            },

            async $_getLatestReport() {
                nprogress.start();
                await this.$store.dispatch('useApiData', {
                    name: 'GetLatestReport'
                }).then((result) => {
                    this.report = result.data;
                    nprogress.done();
                }, reason => {
                    nprogress.done();
                    // Error!
                    const data = {
                        title: this.$t('checkFailed'),
                        message: `${this.$t('checkFailedDueToError')} ${this.$t('errorWas')} "${reason.data.message}"`,
                        variant: 'danger',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                });

                this.controleState = this.controleStateIdle;
            },

            $_initializeWebJobStatuses() {
                // Retrieve available languages
                this.getRefCodes('WEBJOB_STATUS').then(result => {
                    for (const webJobStatus of result.data) {
                        this.webJobStatuses.push({
                            code: webJobStatus.code,
                            description: webJobStatus.description,
                        });
                    }
                });
            },

            $_resetReport() {
                this.report = { messages: [] };
            },

            $_showResults() {
                return this.report !== null && this.status.code !== WEBJOB_STATUS.VERIFICATION_FINISHED;
            },

            /**
             * Are there any errors found?
             * @return {bool} true if there are errors found, false otherwise
             */
            $_errorsFound() {
                return this.report.messages && this.report.messages.length > 0;
            },

            $_setStatusFinished() {
                if (this.webJobStatuses && this.webJobStatuses.length > 0) {
                    this.status = this.webJobStatuses.find(o => o.code === WEBJOB_STATUS.VERIFICATION_FINISHED);
                }
            },

            $_setStatusRequestSent() {
                if (this.webJobStatuses && this.webJobStatuses.length > 0) {
                    this.status = this.webJobStatuses.find(o => o.code === WEBJOB_STATUS.REQUEST_SENT_PLEASE_WAIT);
                }
            },

            $_setStatusError() {
                if (this.webJobStatuses && this.webJobStatuses.length > 0) {
                    this.status = this.webJobStatuses.find(o => o.code === WEBJOB_STATUS.VERIFICATION_ERROR);
                }
            },

            async $_startCheck() {
                this.$_resetReport();

                // Set status to REQUEST_SENT
                this.$_setStatusRequestSent();

                this.controleState = this.controleStatePending;
                this.$_getMessages();
                // queue webjob
                await this.$store.dispatch('useApiData', {
                    name: 'CheckOrganization'
                }).then((result) => {
                    if (result.data?.isQueued) {
                        const message = 'Organisatie controle ingeroosterd. U kunt via het menu naar een ander venster navigeren.';
                        this.showToast({
                            title: null,
                            message: message,
                            variant: 'success',
                            autoHideDelay: 5000,
                        });
                    }
                    this.$_setStatusFinished();
                }, reason => {
                    this.$_setStatusError();
                    // Error!
                    const data = {
                        title: this.$t('checkFailed'),
                        message: `${this.$t('checkFailedDueToError')} ${this.$t('errorWas')} "${reason.data.message}"`,
                        variant: 'danger',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                });
            },
        },

        beforeRouteLeave (to, from, next) {
            // Make sure to close websocket updates after navigating to another page
            this.controleSocket.close();
            // Continue route change
            next();
        }
    };
</script>
